import React from "react"
import { AbsoluteFill, Img, staticFile, useCurrentFrame, useVideoConfig } from "remotion"
import {
  PLATFORM_WATERMARK_TEXT,
  brandWatermarkTop,
  platformWatermarkOpacity,
  platformWatermarkStyle,
} from "../../utils/watermark"
import type { BrandWatermarkPosition } from "../../utils/watermark"

interface BrandWatermark {
  type: "text" | "image"
  text?: string
  imageUrl?: string
  position: BrandWatermarkPosition
  opacity: number
  fontSize?: number
  fontFamily?: string
  color?: string
  x?: number  // percentage 0-100
  y?: number  // percentage 0-100
}

interface WatermarkOverlayProps {
  visible: boolean
  brandWatermark?: BrandWatermark | null
}

const positionStyles: Record<Exclude<BrandWatermark["position"], "custom">, React.CSSProperties> = {
  "top-left": { top: 40, left: 24 },
  "top-right": { top: 40, right: 24 },
  "bottom-left": { bottom: 40, left: 24 },
  "bottom-right": { bottom: 40, right: 24 },
}

export const WatermarkOverlay: React.FC<WatermarkOverlayProps> = ({ visible, brandWatermark }) => {
  const { width, height, fps, durationInFrames } = useVideoConfig()
  const frame = useCurrentFrame()
  const platform = platformWatermarkStyle(width, height)
  const hasBrand = brandWatermark != null
  const hasPlatform = visible

  if (!hasBrand && !hasPlatform) return null

  const brandPlacement: React.CSSProperties = hasBrand
    && brandWatermark!.position === "custom"
    && brandWatermark!.x != null
    && brandWatermark!.y != null
    ? { left: `${brandWatermark!.x}%`, top: `${brandWatermark!.y}%`, transform: "translate(-50%, -50%)" }
    : positionStyles[brandWatermark?.position as keyof typeof positionStyles] || positionStyles["top-right"]

  const brandTop = hasBrand && hasPlatform
    ? brandWatermarkTop(brandWatermark!.position, platform.blockHeight, platform.insetTop)
    : 0

  return (
    <AbsoluteFill>
      {hasBrand && (
        <div
          style={{
            position: "absolute",
            ...brandPlacement,
            ...(brandTop > 0 ? { top: brandTop } : {}),
            opacity: brandWatermark!.opacity,
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {brandWatermark!.type === "image" && brandWatermark!.imageUrl ? (
            <Img
              src={brandWatermark!.imageUrl}
              style={{ maxWidth: 120, display: "block" }}
            />
          ) : (
            <span
              style={{
                display: "inline-block",
                backgroundColor: "rgba(0, 0, 0, 0.35)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                borderRadius: 100,
                padding: `${Math.max(4, (brandWatermark!.fontSize || 18) * 0.3)}px ${Math.max(10, (brandWatermark!.fontSize || 18) * 0.7)}px`,
                color: brandWatermark!.color || "rgba(255, 255, 255, 0.9)",
                fontSize: brandWatermark!.fontSize || 18,
                fontFamily: `${brandWatermark!.fontFamily || "Inter"}, sans-serif`,
                fontWeight: "600",
                letterSpacing: "0.5px",
                // The pill fades with the watermark's opacity; the shadow keeps light text readable on light video.
                textShadow: "0 1px 3px rgba(0, 0, 0, 0.85)",
              }}
            >
              {brandWatermark!.text}
            </span>
          )}
        </div>
      )}

      {hasPlatform && (
        <div
          style={{
            position: "absolute",
            top: platform.insetTop,
            left: platform.insetLeft,
            display: "flex",
            alignItems: "center",
            gap: platform.gap,
            opacity: platformWatermarkOpacity(frame, fps, durationInFrames),
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          <Img
            src={staticFile("favicon.svg")}
            alt=""
            style={{ width: platform.logoSize, height: platform.logoSize, flexShrink: 0 }}
          />
          <span
            style={{
              color: "#FFFFFF",
              fontSize: platform.fontSize,
              fontFamily: "Inter, sans-serif",
              fontWeight: 700,
              textShadow: "0 1px 4px rgba(0, 0, 0, 0.8)",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {PLATFORM_WATERMARK_TEXT}
          </span>
        </div>
      )}
    </AbsoluteFill>
  )
}
